import React, { useState } from 'react';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import SendIcon from '@mui/icons-material/Send';

import { submitQuery } from '../../services/anandaRewardsApi';
import './QueryForm.css';

/**
 * Reusable customer query/complaint form, backed by the Ananda Rewards
 * queries API. Every submitted value — name, mobile, email, query, and the
 * product code — comes from live form state / props, never hardcoded.
 */
function QueryForm({ code = '', className = '', formClassName = '', onSuccess }) {
  const [name, setName] = useState('');
  const [mobile, setMobileNumber] = useState('');
  const [email, setEmail] = useState('');
  const [query, setQuery] = useState('');

  const [status, setStatus] = useState('idle'); // idle | submitting | success | validation | network | error
  const [message, setMessage] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (status === 'submitting') return; // prevent duplicate submissions

    setStatus('submitting');
    setMessage('');
    setFieldErrors({});

    const res = await submitQuery({ name, mobile, email, code, query });

    if (!res.ok) {
      setStatus(res.kind === 'network' ? 'network' : res.kind === 'validation' ? 'validation' : 'error');
      setMessage(res.message);
      setFieldErrors(res.fieldErrors || {});
      return; // keep entered values so the customer can retry
    }

    setStatus('success');
    setMessage(res.data.message);
    setName('');
    setMobileNumber('');
    setEmail('');
    setQuery('');
    if (onSuccess) onSuccess(res.data);
  };

  const submitting = status === 'submitting';

  return (
    <form onSubmit={handleSubmit} className={`query-form ${formClassName}`}>
      <Grid container spacing={2} className={className}>
        {message && (status === 'success' || status === 'error' || status === 'network' || status === 'validation') && (
          <Grid item xs={12}>
            <div className={`query-form-message ${status === 'success' ? 'query-form-message-success' : 'query-form-message-error'}`}>
              {message}
            </div>
          </Grid>
        )}

        <Grid item md={4} xs={12}>
          <TextField
            id="query-form-name"
            label="Name"
            variant="outlined"
            size="small"
            fullWidth
            value={name}
            onChange={(e) => setName(e.target.value)}
            name="name"
            required
            disabled={submitting}
            error={Boolean(fieldErrors.name)}
            helperText={fieldErrors.name || ''}
          />
        </Grid>
        <Grid item md={4} xs={12}>
          <TextField
            id="query-form-mobile"
            label="Mobile"
            variant="outlined"
            size="small"
            fullWidth
            name="mobile"
            value={mobile}
            onChange={(e) => setMobileNumber(e.target.value.replace(/[^0-9]/g, ''))}
            required
            disabled={submitting}
            error={Boolean(fieldErrors.mobile)}
            helperText={fieldErrors.mobile || ''}
          />
        </Grid>
        <Grid item md={4} xs={12}>
          <TextField
            id="query-form-email"
            label="Email"
            variant="outlined"
            size="small"
            fullWidth
            name="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            disabled={submitting}
            error={Boolean(fieldErrors.email)}
            helperText={fieldErrors.email || ''}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            id="query-form-query"
            label="Your Query"
            multiline
            rows={2}
            variant="outlined"
            fullWidth
            name="query"
            required
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            disabled={submitting}
            error={Boolean(fieldErrors.query)}
            helperText={fieldErrors.query || ''}
          />
        </Grid>
        <Grid item xs={12} className="query-form-actions">
          <Button
            variant="contained"
            type="submit"
            disabled={submitting}
            endIcon={<SendIcon fontSize="small" />}
            className="query-form-submit"
          >
            {submitting ? 'Submitting…' : 'Submit Query'}
          </Button>
        </Grid>
      </Grid>
    </form>
  );
}

export default QueryForm;
